/* Public storefront data only. No credentials or copied product inventory. */
(() => {
  if (customElements.get('gww-furniture-finder')) return;
  const split = value => String(value || '').split(',').map(s => s.trim()).filter(Boolean);
  const roomTypes = {
    living: ['chair', 'sofa', 'coffee', 'side', 'bookcase', 'display', 'console', 'bench', 'lighting', 'art', 'ottoman'],
    dining: ['dining', 'chair', 'display', 'console', 'side', 'lighting', 'art'],
    bedroom: ['bed', 'bedside', 'wardrobe', 'bookcase', 'bench', 'chair', 'side', 'lighting', 'art', 'ottoman'],
    office: ['desk', 'bookcase', 'chair', 'side', 'display', 'lighting', 'art'],
    entryway: ['console', 'bench', 'display', 'side', 'lighting', 'art', 'ottoman']
  };
  function kindOf(product) {
    const text = `${product.type} ${product.title}`.toLowerCase();
    const rules = [
      ['bedside', /nightstand|bedside/], ['sofa', /sofa|sectional|couch|daybed/],
      ['ottoman', /ottoman|stool|pouffe?/], ['chair', /chair/], ['coffee', /coffee table|cent(?:er|re) table|teapoy/],
      ['dining', /dining.*table/], ['console', /console/], ['side', /side table|end table|nesting table/],
      ['desk', /desk|study table|writing table/], ['bed', /\bbed\b/], ['wardrobe', /wardrobe|armoire/],
      ['bookcase', /bookcase|book.?shel|shelving|shelf/], ['display', /cabinet|sideboard|chest of drawers/],
      ['bench', /bench/], ['lighting', /lamp|lighting|pendant/], ['art', /wall art|sculpture|wall decor/]
    ];
    return rules.find(([, pattern]) => pattern.test(text))?.[0] || 'other';
  }
  function enrich(product, override = {}) {
    const text = `${product.title} ${product.type} ${product.tags.join(' ')}`.toLowerCase();
    const styles = new Set(split(override.styles));
    [['contemporary', /contemporary|modern/], ['scandinavian', /scandinavian|danish|nordic|japandi/],
      ['mid_century', /mid[ -]century/], ['japanese', /japanese|japandi|wabi[ -]sabi/],
      ['classic', /classic|traditional|antique|colonial|vintage/]].forEach(([key, pattern]) => {
      if (pattern.test(text)) styles.add(key);
    });
    const kind = override.kind || kindOf(product);
    const needs = new Set(split(override.needs));
    if (/storage|drawer|shel|cabinet|wardrobe/.test(text)) needs.add('storage');
    if (['sofa', 'chair', 'bed', 'bench', 'ottoman'].includes(kind)) needs.add('comfort');
    if (/statement|sculptur|art deco|decorative/.test(text)) needs.add('statement');
    return {...product, kind, styles, needs, teak: override.teak === true || /\bteak\b/.test(text), search: `${text} ${product.collections.join(' ')}`};
  }
  function node(tag, className, text) {
    const element = document.createElement(tag);
    if (className) element.className = className;
    if (text !== undefined) element.textContent = text;
    return element;
  }
  class GwwLiveFinder extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.config = JSON.parse(this.querySelector('[data-gww-config]').textContent);
      this.form = this.querySelector('[data-filters]');
      this.typeSelect = this.querySelector('[data-type]');
      this.collectionSelect = this.querySelector('[data-collection]');
      this.search = this.querySelector('[data-search]');
      this.sort = this.querySelector('[data-sort]');
      this.grid = this.querySelector('[data-cards]');
      this.syncStatus = this.querySelector('[data-sync]');
      this.refreshButton = this.querySelector('[data-refresh]');
      this.dimensions = this.querySelector('[data-dimensions]');
      this.products = [];
      this.collections = [];
      this.all = false;
      this.hasLoaded = false;
      this.form.addEventListener('submit', event => event.preventDefault());
      this.form.addEventListener('change', () => { this.all = false; this.update(); });
      this.search.addEventListener('input', () => {
        clearTimeout(this.searchTimer);
        this.searchTimer = setTimeout(() => { this.all = false; this.update(); }, 120);
      });
      this.sort.addEventListener('change', () => this.update());
      this.form.addEventListener('reset', () => requestAnimationFrame(() => {
        this.typeSelect.value = 'all';
        this.collectionSelect.value = 'all';
        this.sort.value = 'title';
        this.dimensions.value = '';
        this.all = false;
        this.update();
      }));
      this.querySelector('[data-clear-refinements]').addEventListener('click', () => this.form.reset());
      this.querySelector('[data-show-all]').addEventListener('click', () => { this.all = true; this.update(); });
      this.dimensions.addEventListener('input', () => this.updateLinks());
      this.refreshButton.addEventListener('click', () => this.refresh(true));
      this.visibilityHandler = () => { if (!document.hidden && Date.now() - (this.lastSync || 0) > 10000) this.refresh(); };
      document.addEventListener('visibilitychange', this.visibilityHandler);
      this.focusHandler = () => this.visibilityHandler();
      window.addEventListener('focus', this.focusHandler);
      window.addEventListener('pageshow', this.focusHandler);
      this.timer = setInterval(() => { if (!document.hidden) this.refresh(); }, 60000);
      this.updateLinks();
      this.refresh(true);
    }
    disconnectedCallback() {
      clearInterval(this.timer);
      clearTimeout(this.searchTimer);
      document.removeEventListener('visibilitychange', this.visibilityHandler);
      window.removeEventListener('focus', this.focusHandler);
      window.removeEventListener('pageshow', this.focusHandler);
      this.controller?.abort();
      // A section editor inserts a new element when re-rendering the section.
    }
    selected(group) { return this.querySelector(`[data-${group}]:checked`)?.value || 'all'; }
    async loadPages(path, kind, signal, revision) {
      const records = new Map();
      let total = null;
      let pages = 1;
      for (let page = 1; page <= pages; page++) {
        const url = new URL(path, location.origin);
        if (url.origin !== location.origin) throw new Error('Unexpected feed origin');
        url.searchParams.set('view', 'gww-finder-data');
        url.searchParams.set('page', String(page));
        url.searchParams.set('_gww', String(revision));
        const preview = new URLSearchParams(location.search).get('preview_theme_id');
        if (preview) url.searchParams.set('preview_theme_id', preview);
        if (kind === 'products') url.searchParams.set('sort_by', 'title-ascending');
        const response = await fetch(url.href, {credentials: 'same-origin', cache: 'no-store', signal});
        if (!response.ok) throw new Error(`Catalogue request failed: ${response.status}`);
        const documentFeed = new DOMParser().parseFromString(await response.text(), 'text/html');
        const content = documentFeed.querySelector('script[data-gww-feed]');
        if (!content) throw new Error('Missing catalogue feed');
        const data = JSON.parse(content.textContent);
        if (data.version !== 1 || data.kind !== kind || data.page !== page || !Array.isArray(data.items)
          || !Number.isInteger(data.total) || data.total < 0 || !Number.isInteger(data.pages) || data.pages < 0)
          throw new Error('Invalid catalogue page');
        if (total !== null && (total !== data.total || pages !== Math.max(1, data.pages))) throw new Error('Catalogue changed while loading');
        total = data.total;
        pages = Math.max(1, data.pages);
        // Shopify signals a pagination limit at 25,001. Never silently truncate.
        if (total > 25000 || pages > 500) throw new Error('Catalogue exceeds storefront pagination limit');
        if (page < pages && !data.items.length) throw new Error('Incomplete catalogue page');
        for (const item of data.items) {
          if (!item.id || !item.handle || typeof item.title !== 'string') throw new Error('Invalid catalogue item');
          if (kind === 'products' && (!Array.isArray(item.tags) || !Array.isArray(item.collections) || !Number.isFinite(item.price)))
            throw new Error('Invalid product data');
          records.set(item.id, item);
        }
      }
      if (records.size !== total) throw new Error('Incomplete catalogue; retry required');
      return [...records.values()];
    }
    async refresh(manual = false) {
      if (this.loading || !this.isConnected) return;
      this.loading = true;
      this.refreshButton.disabled = true;
      if (!this.hasLoaded || manual) this.syncStatus.textContent = this.hasLoaded ? this.config.refreshing : this.config.loading;
      this.controller = new AbortController();
      const deadline = setTimeout(() => this.controller.abort(), 45000);
      try {
        const revision = Date.now();
        const [collections, products] = await Promise.all([
          this.loadPages(this.config.collectionsUrl, 'collections', this.controller.signal, revision),
          this.loadPages(this.config.productsUrl, 'products', this.controller.signal, revision)
        ]);
        const fingerprint = JSON.stringify([collections, products]);
        const changed = fingerprint !== this.fingerprint;
        if (changed) {
          const overrides = new Map(this.config.overrides.filter(o => o.handle).map(o => [o.handle, o]));
          this.products = products.map(product => enrich(product, overrides.get(product.handle)));
          this.collections = collections.sort((a, b) => a.title.localeCompare(b.title));
          const previousCollection = this.collectionSelect.value;
          const currentType = this.typeSelect.value;
          this.collectionSelect.replaceChildren(new Option(this.config.allCollections, 'all'),
            ...this.collections.map(collection => new Option(`${collection.title} (${collection.count})`, collection.handle)));
          this.collectionSelect.value = this.collections.some(c => c.handle === previousCollection) ? previousCollection : 'all';
          const kinds = [...new Set(this.products.map(p => p.kind))].sort((a, b) => this.config.typeLabels[a].localeCompare(this.config.typeLabels[b]));
          this.typeSelect.replaceChildren(new Option(this.config.typeLabels.all, 'all'), ...kinds.map(kind => new Option(this.config.typeLabels[kind], kind)));
          this.typeSelect.value = kinds.includes(currentType) ? currentType : 'all';
          this.fingerprint = fingerprint;
          this.hasLoaded = true;
          this.update();
        }
        this.lastSync = Date.now();
        this.syncStatus.textContent = this.config.syncSuccess.replace('[products]', this.products.length).replace('[collections]', this.collections.length)
          .replace('[time]', new Date(this.lastSync).toLocaleTimeString([], {hour: '2-digit', minute: '2-digit', second: '2-digit'}));
      } catch (error) {
        if (this.isConnected) this.syncStatus.textContent = this.hasLoaded ? this.config.refreshError : this.config.syncError;
      } finally {
        clearTimeout(deadline);
        this.loading = false;
        this.refreshButton.disabled = false;
        this.grid.setAttribute('aria-busy', 'false');
      }
    }
    update() {
      if (!this.hasLoaded) return;
      const room = this.selected('room'), style = this.selected('style'), need = this.selected('need'), wood = this.selected('wood');
      const collection = this.collectionSelect.value;
      const words = this.search.value.trim().toLowerCase().split(/\s+/).filter(Boolean);
      const matches = this.products.filter(product =>
        (collection === 'all' || product.collections.includes(collection)) &&
        (room === 'all' || roomTypes[room]?.includes(product.kind)) &&
        (this.typeSelect.value === 'all' || product.kind === this.typeSelect.value) &&
        (style === 'open' || product.styles.has(style)) &&
        (need === 'any' || product.needs.has(need)) &&
        (wood !== 'teak' || product.teak) && words.every(word => product.search.includes(word)));
      matches.sort((a, b) => this.sort.value === 'price-low' ? a.price - b.price || a.title.localeCompare(b.title) :
        this.sort.value === 'price-high' ? b.price - a.price || a.title.localeCompare(b.title) : a.title.localeCompare(b.title));
      const shown = this.all ? matches : matches.slice(0, 24);
      this.grid.replaceChildren(...shown.map(product => this.card(product)));
      this.querySelector('[data-empty]').hidden = matches.length > 0;
      const showAll = this.querySelector('[data-show-all]');
      showAll.hidden = shown.length === matches.length;
      showAll.textContent = this.config.showAll.replace('[count]', matches.length);
      const heading = collection === 'all' ? this.config.catalogHeading : this.config.collectionHeading.replace('[collection]', this.collections.find(c => c.handle === collection)?.title || '');
      this.querySelector('[data-results-heading]').textContent = heading;
      this.querySelector('[data-count]').textContent = matches.length ? this.config.count.replace('[count]', matches.length).replace('[shown]', shown.length) : this.config.emptyCount;
      this.querySelector('[data-mobile-count]').textContent = `(${matches.length})`;
      this.updateLinks();
    }
    card(product) {
      const card = node('article', 'gww-card');
      card.dataset.card = '';
      card.dataset.productTitle = product.title;
      card.dataset.productUrl = product.url;
      card.dataset.productHandle = product.handle;
      const photo = node('a', 'gww-photo');
      photo.href = product.url;
      photo.setAttribute('aria-label', product.title);
      if (product.image) {
        const image = node('img');
        image.src = product.image.src;
        image.srcset = `${product.image.small} 400w, ${product.image.medium} 700w, ${product.image.src} 1000w`;
        image.sizes = '(max-width: 600px) 90vw, (max-width: 900px) 44vw, 420px';
        image.width = product.image.width;
        image.height = product.image.height;
        image.alt = product.image.alt;
        image.loading = 'lazy';
        image.decoding = 'async';
        photo.append(image);
      } else photo.append(node('span', '', this.config.imageMissing));
      const info = node('div', 'gww-card-info');
      info.append(node('p', 'gww-eyebrow', this.config.typeLabels[product.kind]));
      const heading = node('h3');
      const title = node('a', '', product.title);
      title.href = product.url;
      heading.append(title);
      info.append(heading, node('p', 'gww-price', (product.priceVaries ? this.config.from : '') + product.priceText));
      if (!product.available) info.append(node('p', 'gww-stock', this.config.soldOut));
      if (product.description) info.append(node('p', '', product.description));
      const actions = node('div', 'gww-card-actions');
      const enquiry = node('a', 'gww-button', this.config.enquire + ' ↗');
      enquiry.dataset.productEnquiry = '';
      enquiry.href = this.enquiryUrl(card);
      enquiry.target = '_blank';
      enquiry.rel = 'noopener';
      const view = node('a', 'gww-product-link', this.config.viewProduct + ' ↗');
      view.href = product.url;
      actions.append(enquiry, view);
      info.append(actions);
      card.append(photo, info);
      return card;
    }
    enquiryUrl(card) {
      const c = this.config;
      const lines = [c.messageIntro + (card ? card.dataset.productTitle : c.messageCustom) + '.',
        c.messageRoom + c.roomLabels[this.selected('room')], c.messageType + c.typeLabels[this.typeSelect.value || 'all'],
        c.messageStyle + c.styleLabels[this.selected('style')], c.messageNeed + c.needLabels[this.selected('need')],
        c.messageWood + (this.selected('wood') === 'teak' ? c.woodTeak : c.woodOpen)];
      if (this.dimensions.value.trim()) lines.push(c.messageDimensions + this.dimensions.value.trim());
      if (card) lines.push(c.messageReference + new URL(card.dataset.productUrl, location.origin).href);
      lines.push(c.messageEnd);
      return `https://wa.me/${c.phone.replace(/\D/g, '')}?text=${encodeURIComponent(lines.join('\n'))}`;
    }
    updateLinks() {
      this.querySelector('[data-custom-enquiry]').href = this.enquiryUrl();
      this.grid.querySelectorAll('[data-card]').forEach(card => { card.querySelector('[data-product-enquiry]').href = this.enquiryUrl(card); });
    }
  }
  customElements.define('gww-furniture-finder', GwwLiveFinder);
})();
